import React from 'react';
import { BUCKETS } from './bucket-theme';
import { DoneToggleButton } from './done-toggle-button';
import { TASK_TYPES } from 'shared/src/wrkthru/task-types';
import type { WrkthruTask, ZohoTaskStatus } from 'shared/src/wrkthru/types';
import './wrkthru.css';

type TaskCardProps = {
  task: WrkthruTask;
  onWrkthruIt?: (task: WrkthruTask) => void;
  onMarkDone?: () => void;
  onUndone?: () => void;
};

const STATUS_STYLE: Record<ZohoTaskStatus, string> = {
  'Not Started': 'bg-slate-100 text-slate-700',
  'In Progress': 'bg-blue-100 text-blue-700',
  'Waiting on someone else': 'bg-amber-100 text-amber-800',
  Deferred: 'bg-slate-100 text-slate-500',
  Completed: 'bg-emerald-100 text-emerald-700'
};

export function TaskCard({ task, onWrkthruIt, onMarkDone, onUndone }: TaskCardProps) {
  const typeMeta = TASK_TYPES[task.taskType.category];
  const bucket = task.bucket ?? 'P3';
  const accent = BUCKETS[bucket].accent;

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <span
        className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: accent.hex }}
        aria-hidden="true"
      />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span
            className="inline-flex items-center rounded px-1.5 py-0.5 font-semibold uppercase tracking-wide"
            style={{ backgroundColor: typeMeta.hex.bg, color: typeMeta.hex.text }}>
            {task.taskType.label}
          </span>
          <span className="inline-flex items-center rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-semibold uppercase tracking-wide text-slate-500">
            Zoho Task
          </span>
          <span
            className={`rounded px-1.5 py-0.5 font-semibold uppercase tracking-wide ${STATUS_STYLE[task.status]}`}>
            {task.status}
          </span>
        </div>

        <h3 className="mt-1 text-base font-semibold leading-snug text-slate-900">{task.title}</h3>

        <div className="mt-0.5 text-xs text-slate-500">
          <a href={task.relatedCampaignHref} className="hover:underline">
            {task.relatedCampaignName}
          </a>
          {task.dueDate && <span className="ml-2 text-slate-400">· due {task.dueDate}</span>}
        </div>

        <div className="wrkthru-llm-card mt-2">
          <p className="text-sm leading-snug text-slate-700">{task.description}</p>
          {task.whyThisPriority && (
            <p className="mt-1.5 text-xs leading-snug text-slate-600">
              <span className="font-semibold text-slate-700">Reasoning:</span>{' '}
              {task.whyThisPriority}
            </p>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-col items-stretch gap-2 self-start">
        <DoneToggleButton onMarkDone={onMarkDone} onUndone={onUndone} />
        <a
          href={task.zohoHref}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
          View on Zoho
        </a>
        <button
          type="button"
          onClick={() => onWrkthruIt?.(task)}
          className="wrkthru-gradient-btn px-3 py-1.5 text-xs">
          <span className="wrkthru-gradient-text font-semibold">Wrkthru_ it</span>
        </button>
      </div>
    </div>
  );
}
